import { BaseQueryFn } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import {
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query/react';
import { getEncryptParams, getKcToken } from 'src/keycloak';
import { setLicenseExpired } from 'src/redux/slices/auth';
import { ErrorCodes } from 'src/utils/errorMappings';
import { BASE_SERVER_URL, IS_ENCRYPTION_ENABLED } from '../config';
import { decryptResponse, serializeErrorResponse } from './utils';

export const API_BASE_URL = `/api/v1/`;
export const API_MS_BASE_URL = '/api/';
export const CHAT_MS_BASE_URL = 'chat/v1/';
export const CORE_MS_BASE_URL = 'core/v1/';

export type ExtendedFetchArgs = FetchArgs & {
  returnUnparsedResponse?: boolean;
  ms?: boolean;
};

const handleGlobalErrors = (api, response) => {
  const state = api.getState();
  const err: { error: FetchBaseQueryError } = serializeErrorResponse(response);
  if ((err?.error.status as string) === ErrorCodes.USER_LICENSE_EXPIRED) {
    if (!state.auth.isLicenseExpired) {
      api.dispatch(setLicenseExpired({ isLicenseExpired: true }));
    }
  } // Other Global errors go here
  return err;
};

export const customBaseQuery: BaseQueryFn<
  ExtendedFetchArgs,
  unknown,
  FetchBaseQueryError,
  {},
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  if (!args.responseHandler) {
    {
      args.responseHandler = IS_ENCRYPTION_ENABLED ? 'text' : 'json';
    }
  }
  const baseUrl = `${BASE_SERVER_URL}${
    args?.ms ? API_MS_BASE_URL : API_BASE_URL
  }`;
  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      const token = getKcToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('withCredentials', 'true');
      return headers;
    }
  });
  const encKeyBeforeTokenRefresh = getEncryptParams()?.k;

  const result = await baseQuery(args, api, extraOptions);

  if (args.returnUnparsedResponse) {
    return result;
  }
  let decryptKey = getEncryptParams()?.k;
  let response = decryptResponse(result, decryptKey);
  if (!response) {
    response = decryptResponse(result, encKeyBeforeTokenRefresh);
  }

  if (result.error || !response) {
    return handleGlobalErrors(api, response);
  }
  return { data: response.ret_val };
};
